import { useState } from "react";
import type { HTMLAttributes } from "react";
import { Bot, ChevronRight, SearchX } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ProductSearchPicker } from "modules/lists/components/product-search-picker";
import { useCart } from "modules/cart/hooks/useCart";
import { CandidateProduct } from "modules/lists/types/lists";
import type { CartItem, Payload } from "../types/cart";
import type { StoreData } from "modules/main/types/storeData";
import { get_total_qty_per_prod } from "utils/cartUtils";
import Amount from "utils/amount";
import { AddDeleteCartButtons } from "./cartElements";
import { FreeItemReplacementSection } from "./free-item-replacement-section";
import { ProductSheetDialog } from "./product-sheet-dialog";
import { candidateFromCartItem } from "../utils/candidate-utils";

interface CartElementProps extends HTMLAttributes<HTMLDivElement> {
  cart: CartItem[];
  item: CartItem;
  storeList: StoreData[];
  addDeletefromCart: (payload: Payload) => void;
  removeFromCart: (payload: Payload) => void;
}

/**
 * Renders a single product row inside the cart sheet.
 *
 * @param props - Cart item data and mutation handlers required to update the cart.
 * @returns A product row card, or null when the item has no price component.
 */
export const CartElement = ({ item, ...props }: CartElementProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [promotingCandidateId, setPromotingCandidateId] = useState<
    string | null
  >(null);
  const { setProductDecision, swapCartItem } = useCart();
  const baseProduct = item.price_component[0];
  const price = item.price_component.reduce((acc, pc) => {
    return acc + pc.price;
  }, 0);
  const categoryId = item.category_id;
  const qty = get_total_qty_per_prod(item.id, props.cart);

  if (!baseProduct) {
    // eslint-disable-next-line no-console
    console.warn(
      "[CartElement] item without price_component, skipping render",
      item,
    );
    return null;
  }

  const imageUrl = baseProduct.images?.thumbs ?? baseProduct.image_url;

  const handleQuantityChange = (payload: Payload) => {
    props.addDeletefromCart({
      ...payload,
      is_ai_resolved: item.is_ai_resolved,
      raw_description: item.raw_description,
      productDecision: item.productDecision,
      alternativeCandidates: item.alternativeCandidates,
    });
  };

  const handleCandidatePromoted = async (
    candidate: CandidateProduct,
    candidates: CandidateProduct[],
    activeCandidates: CandidateProduct[],
  ) => {
    const candidateKey = (c: CandidateProduct) => c.sku ?? c.id;
    const promotedSku = candidateKey(candidate);
    const demoted = candidateFromCartItem(item);
    // Keep every candidate except the promoted one, so previously discarded
    // alternatives stay visible (struck through) after the swap.
    const swappedCandidates = [
      demoted,
      ...candidates.filter((c) => candidateKey(c) !== promotedSku),
    ];
    // Only active candidates (plus the demoted match) become replacement ids.
    const replacementIds = [
      candidateKey(demoted),
      ...activeCandidates
        .filter((c) => candidateKey(c) !== promotedSku)
        .map(candidateKey),
    ];
    setPromotingCandidateId(promotedSku);
    try {
      await swapCartItem({
        sourceItemId: item.id,
        replacement: {
          id: promotedSku,
          sku: promotedSku,
          name: candidate.name,
          category_id: categoryId,
          quantity: qty,
          is_ai_resolved: true,
          raw_description: item.raw_description ?? baseProduct.name,
          alternativeCandidates: swappedCandidates,
          productDecision: {
            productId: candidate.canonicalSku,
            action: "selected-replacement",
            replacementIds,
          },
        },
      });
    } finally {
      setPromotingCandidateId(null);
    }
  };

  return (
    <Card className="text-sm w-full p-3 mt-1 overflow-hidden">
      <CardContent className="flex flex-col p-0 text-xs py-1 items-start">
        {item.is_ai_resolved && (
          <div className="flex items-center w-full justify-between bg-orange-50 text-xxs text-muted-foreground font-medium rounded-sm px-2 py-2 max-w-full overflow-hidden mb-2">
            <div className="flex items-center gap-1 min-w-0 overflow-hidden">
              <Bot className="shrink-0 size-4" />
              <span className="whitespace-nowrap">Mejor match para:</span>
              <span className="font-semibold truncate min-w-0">
                "{item.raw_description}"
              </span>
            </div>
            <SearchX
              className="shrink-0 size-5 cursor-pointer text-destructive"
              onClick={() => setPickerOpen(true)}
              data-testid="searchx-picker-trigger"
            />
          </div>
        )}
        <div
          className={cn("flex gap-3 text-xs min-w-0 overflow-hidden w-full", {
            "border-l-2 border-primary/50": item.is_ai_resolved,
          })}
        >
          <div className="shrink-0">
            <ProductSheetDialog productID={item.slug} ariaLabel={baseProduct.name}>
              <button
                type="button"
                className="block"
                aria-label={baseProduct.name}
              >
                <img className="Image-60" src={imageUrl} alt="" />
              </button>
            </ProductSheetDialog>
          </div>
          <div className="flex flex-col items-start min-h-20 w-full gap-1 min-w-0">
            <div className="flex flex-row gap-2 items-start justify-between w-full min-w-0 overflow-hidden">
              <ProductSheetDialog productID={item.slug} ariaLabel={baseProduct.name}>
                <button
                  type="button"
                  className="flex min-w-0 items-start gap-1 overflow-hidden text-left"
                  aria-label={baseProduct.name}
                >
                  <span className="min-w-0 flex-1">
                    <p className="font-semibold min-w-0 overflow-hidden">
                      {baseProduct.name}
                    </p>
                    {item.brand && item.brand !== baseProduct.name && (
                      <p className="text-muted-foreground text-xxs truncate min-w-0">
                        {item.brand}
                      </p>
                    )}
                  </span>
                  <ChevronRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                </button>
              </ProductSheetDialog>
            </div>
            <p className="text-muted-foreground font-semibold">
              <Amount amount={price / qty} currency="CLP" locale="es-CL" />
            </p>
            <div className="flex flex-row justify-between items-center w-full mt-1 gap-1">
              <AddDeleteCartButtons
                className=""
                product_id={item.id}
                category_id={categoryId}
                sku={item.price_component[0]?.sku}
                name={item.price_component[0]?.name}
                quantity={qty}
                displayQuantity={qty}
                showDelete={false}
                method={item.method}
                PlusMinusCallback={handleQuantityChange}
                RemoveCallBack={props.removeFromCart}
              />
              <span className="font-bold text-sm">
                <Amount amount={price} currency="CLP" locale="es-CL" />
              </span>
            </div>
            <div className="flex flex-col mt-3">
              {item.is_ai_resolved && pickerOpen && (
                <ProductSearchPicker
                  open={pickerOpen}
                  onOpenChange={setPickerOpen}
                  initialQuery={item.raw_description || baseProduct.name}
                  onProductSelected={(payload) => {
                    props.removeFromCart({
                      id: item.id,
                      category_id: categoryId,
                      sku: baseProduct.sku,
                      quantity: 0,
                    });
                    props.addDeletefromCart({ ...payload, quantity: qty });
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div
          className={cn(
            "flex text-xs min-w-0 overflow-hidden w-full pl-4 pr-2",
            {
              "border-l-2 border-primary/50": item.is_ai_resolved,
            },
          )}
        >
          <FreeItemReplacementSection
            product={item}
            onDecisionConfirmed={(decision) =>
              setProductDecision(item.id, decision)
            }
            onCandidatePromoted={handleCandidatePromoted}
            promotingCandidateId={promotingCandidateId}
            onManualProductSelected={(payload: Payload) => {
              props.removeFromCart({
                id: item.id,
                category_id: categoryId,
                sku: baseProduct.sku,
                quantity: 0,
              });
              props.addDeletefromCart({ ...payload, quantity: qty });
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};
